import React, { useState, useEffect, useRef } from "react";
import { ChatMessage } from "@/components/AI/chat/ChatMessage";
import Icon, { ICONS } from "@/components/Core/Icons";
import { cn } from "@/lib/utils";
import {
  ManualAssistantMessage,
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";

export interface ChatListProps {
  messages: (Message | ManualAssistantMessage)[];
  conversationId: string;
  searchQuery?: string;
}

export const ChatList: React.FC<ChatListProps> = React.memo(
  ({ messages, conversationId, searchQuery }) => {
    const loadMoreMessages = useSupabaseStore(
      (state) => state.loadMoreMessages
    );
    const pagination = useSupabaseStore(
      (state) => state.aiChatMessagesPagination[conversationId]
    );
    const useReasoning = useAiContextStore((state) => state.useReasoning);
    const isChatLoading = useIsChatLoading();
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
    const [isAtScrollBottom, setIsAtScrollBottom] = useState(true);

    const filteredMessages = messages.filter(
      (msg) => (msg as Message).role !== "tool-result"
    );

    // Reset scroll position when changing conversations
    useEffect(() => {
      if (conversationId && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        setIsAtScrollBottom(true);
      }

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, [conversationId]);

    useEffect(() => {
      if (isAtScrollBottom && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, [filteredMessages, isAtScrollBottom]);

    const handleScroll = () => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      // Track whether we're at the bottom of the scroll container
      const atBottom = scrollTop + clientHeight >= scrollHeight - 15;
      setIsAtScrollBottom(atBottom);

      // Only try to load more messages when:
      // 1. User is near the top of the container
      // 2. We have more messages to load
      // 3. We're not already loading
      if (scrollTop < 30 && pagination?.hasMore && !pagination?.isLoading) {
        // Debounce the load request to avoid triggering during fast scrolls
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          if (containerRef.current && containerRef.current.scrollTop < 30) {
            loadMoreMessages(conversationId, supabaseClient);
          }
        }, 150);
      }
    };

    if (!messages.length) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        className="relative mx-auto max-w-2xl pl-2 pr-0 pb-12 overflow-y-scroll h-full pt-4"
        onScroll={handleScroll}
      >
        {pagination?.isLoading && (
          <div className="text-center py-1 text-gray-500 text-xs">
            Loading messages...
          </div>
        )}

        {pagination?.noMoreMessages && (
          <div className="text-center py-1 text-gray-500 text-xs">
            No more messages
          </div>
        )}

        {filteredMessages.map((message, index) => (
          <div key={`${message.id || index}`}>
            <ChatMessage
              message={message as Message}
              isLastMessage={index === filteredMessages.length - 1}
              searchQuery={searchQuery}
            />
            {index < filteredMessages.length - 1 && <span className="my-1" />}
          </div>
        ))}

        {isChatLoading &&
          (filteredMessages?.[filteredMessages?.length - 1] as Message)
            ?.role !== "assistant" &&
          !(
            filteredMessages?.[
              filteredMessages?.length - 1
            ] as ManualAssistantMessage
          )?.progressItems?.length && (
            <div>
              <div className="flex flex-row gap-1 items-center">
                <div
                  className={cn(
                    "flex h-6 w-6 shrink-0 select-none items-center justify-center rounded-md shadow",
                    "bg-primary-0 text-primary-foreground",
                    "p-0"
                  )}
                >
                  <Icon icon={ICONS.AI_LOGO} className="w-4 h-4 p-1" />
                </div>
                <div className="text-xs text-secondary-n-50">Tempo AI</div>
              </div>

              <div className="pl-1 mt-2">
                {useReasoning ? (
                  <p className="text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-white to-gray-900 animate-shimmer bg-[length:200%_100%]">
                    Thinking
                  </p>
                ) : (
                  <img
                    src="/icons/ai-chat-status/pending.svg"
                    className="size-4 shrink-0 flex animate-pulse"
                  />
                )}
              </div>
            </div>
          )}
      </div>
    );
  }
);

ChatList.displayName = "ChatList";
